import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Moon, Monitor, Sun } from 'lucide-react';
import { useAuth, type ViewMode } from '../../../context/AuthContext';
import { useTheme, type UIDensity } from '../../../context/ThemeContext';
import { ModuleCard } from '../../shared/components/ModulePageHeader';
import {
  getUserPreferences,
  upsertUserPreferences,
  type PreferencesPayload,
} from '../../../services/userPreferencesService';

type Language = 'pt-BR' | 'en-US' | 'es-ES';

const LANGUAGE_KEY = 'workflow_language_preference';
const PREFERENCES_LOCAL_KEY = 'workflow_user_preferences_snapshot';
const SYNC_DEBOUNCE_MS = 600;

const readLocalSnapshot = (): PreferencesPayload => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(PREFERENCES_LOCAL_KEY);
    return raw ? (JSON.parse(raw) as PreferencesPayload) : {};
  } catch {
    return {};
  }
};

const writeLocalSnapshot = (patch: PreferencesPayload) => {
  if (typeof window === 'undefined') return;
  try {
    const current = readLocalSnapshot();
    window.localStorage.setItem(
      PREFERENCES_LOCAL_KEY,
      JSON.stringify({ ...current, ...patch }),
    );
  } catch {
    /* ignore */
  }
};

export const AccountPreferencesPage: React.FC = () => {
  const { theme, setTheme, density, setDensity } = useTheme();
  const { viewMode, setViewMode, user } = useAuth();
  const userId = user?.id ?? null;

  const initialLanguage = useMemo<Language>(() => {
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(LANGUAGE_KEY);
      if (stored === 'pt-BR' || stored === 'en-US' || stored === 'es-ES') return stored;
    }
    const snap = readLocalSnapshot();
    if (snap.language === 'pt-BR' || snap.language === 'en-US' || snap.language === 'es-ES') {
      return snap.language;
    }
    return 'pt-BR';
  }, []);

  const [language, setLanguage] = useState<Language>(initialLanguage);

  // ── Hidratação remota: Supabase > LocalStorage > defaults ──────────
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      const remote = await getUserPreferences(userId);
      if (cancelled || !remote) return;

      if (remote.theme && remote.theme !== theme) setTheme(remote.theme);
      if (remote.viewMode && remote.viewMode !== viewMode) setViewMode(remote.viewMode);
      if (remote.density && remote.density !== density) setDensity(remote.density);
      if (remote.language && remote.language !== language) {
        setLanguage(remote.language);
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(LANGUAGE_KEY, remote.language);
        }
      }
      writeLocalSnapshot(remote);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // ── Sync debounced para Supabase + LocalStorage ────────────────────
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queueSync = (patch: PreferencesPayload) => {
    writeLocalSnapshot(patch);
    if (!userId) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      void upsertUserPreferences(userId, patch);
    }, SYNC_DEBOUNCE_MS);
  };

  useEffect(
    () => () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    },
    [],
  );

  // ── Handlers ───────────────────────────────────────────────────────
  const handleThemeChange = (value: 'light' | 'midnight' | 'black') => {
    setTheme(value);
    queueSync({ theme: value });
  };

  const handleViewModeChange = (value: ViewMode) => {
    setViewMode(value);
    queueSync({ viewMode: value });
  };

  const handleDensityChange = (value: UIDensity) => {
    setDensity(value);
    queueSync({ density: value });
  };

  const handleLanguageChange = (value: Language) => {
    setLanguage(value);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(LANGUAGE_KEY, value);
    }
    queueSync({ language: value });
  };

  return (
    <div className="enterprise-page h-full overflow-y-auto">
      <ModuleCard title="Tema">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleThemeChange('light')}
            className={`h-[var(--input-height)] px-4 rounded-md border text-sm font-medium inline-flex items-center gap-2 ${
              theme === 'light'
                ? 'border-enterprise-primary text-enterprise-primary bg-enterprise-active/80'
                : 'border-enterprise-border-strong text-enterprise-text-primary bg-enterprise-panel hover:bg-enterprise-active/40'
            }`}
          >
            <Sun size={14} />
            Claro
          </button>
          <button
            type="button"
            onClick={() => handleThemeChange('midnight')}
            className={`h-[var(--input-height)] px-4 rounded-md border text-sm font-medium inline-flex items-center gap-2 ${
              theme === 'midnight'
                ? 'border-enterprise-primary text-enterprise-primary bg-enterprise-active/80'
                : 'border-enterprise-border-strong text-enterprise-text-primary bg-enterprise-panel hover:bg-enterprise-active/40'
            }`}
          >
            <Moon size={14} />
            Escuro
          </button>
          <button
            type="button"
            onClick={() => handleThemeChange('black')}
            className={`h-[var(--input-height)] px-4 rounded-md border text-sm font-medium inline-flex items-center gap-2 ${
              theme === 'black'
                ? 'border-enterprise-primary text-enterprise-primary bg-enterprise-active/80'
                : 'border-enterprise-border-strong text-enterprise-text-primary bg-enterprise-panel hover:bg-enterprise-active/40'
            }`}
          >
            <Monitor size={14} />
            Black
          </button>
        </div>
      </ModuleCard>

      <ModuleCard title="Visualização">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-[var(--block-gap)]">
          <Field label="Modo de visualização">
            <select
              value={viewMode}
              onChange={(event) => handleViewModeChange(event.target.value as ViewMode)}
              className="w-full h-[var(--input-height)] px-3 border border-enterprise-border-strong rounded-md bg-enterprise-panel text-sm text-enterprise-text-primary focus:outline-none focus:ring-1 focus:ring-enterprise-primary"
            >
              <option value="auto">Automático</option>
              <option value="admin">Admin</option>
              <option value="gestor">Gestor</option>
              <option value="usuario">Usuário</option>
            </select>
          </Field>
          <Field label="Densidade da interface">
            <select
              value={density}
              onChange={(event) => handleDensityChange(event.target.value as UIDensity)}
              className="w-full h-[var(--input-height)] px-3 border border-enterprise-border-strong rounded-md bg-enterprise-panel text-sm text-enterprise-text-primary focus:outline-none focus:ring-1 focus:ring-enterprise-primary"
            >
              <option value="compact">Compacta</option>
              <option value="normal">Normal</option>
              <option value="comfortable">Confortável</option>
            </select>
          </Field>
        </div>
      </ModuleCard>

      <ModuleCard title="Idioma">
        <Field label="Idioma da interface">
          <select
            value={language}
            onChange={(event) => handleLanguageChange(event.target.value as Language)}
            className="w-full md:w-80 h-[var(--input-height)] px-3 border border-enterprise-border-strong rounded-md bg-enterprise-panel text-sm text-enterprise-text-primary focus:outline-none focus:ring-1 focus:ring-enterprise-primary"
          >
            <option value="pt-BR">Português (Brasil)</option>
            <option value="en-US">English (US)</option>
            <option value="es-ES">Español</option>
          </select>
        </Field>
        <p className="mt-3 text-xs enterprise-text-secondary">
          Preparado para internacionalização. O idioma selecionado será aplicado nas próximas versões.
        </p>
      </ModuleCard>
    </div>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <label className="enterprise-field-label mb-1.5 block">{label}</label>
    {children}
  </div>
);
